import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { CLOSURE_REASON_MAX, VAULTS_NS as NS } from './constants';
import type { VaultClosureForm } from './useVaultClosure';

/**
 * XBIT-4243: the plain confirm for a closure request (with a reason, kept on the audit row), a second operator's
 * approval or a cancel. Module PRD §10 says the confirm dialog's design is not in the Figma and owes it; this is plain
 * until it exists.
 */
export const ClosureDialog = ({
  form,
  vaultName,
}: {
  form: VaultClosureForm;
  vaultName: string;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) =>
    translate(`${NS}.detail.closure.dialog.${key}`, options);
  const { mode } = form;
  const count = Array.from(form.reason.trim()).length;

  return (
    <Dialog
      open={mode !== null}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      {mode && (
        <>
          <DialogTitle>{t(`${mode}Title`, { name: vaultName })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">{t(`${mode}Body`)}</Typography>
              {form.alert && (
                <Alert severity={form.alert.severity}>
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                    {form.alert.messages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Stack>
                </Alert>
              )}
              {mode === 'request' && (
                <TextField
                  required
                  fullWidth
                  multiline
                  minRows={3}
                  label={t('reason')}
                  value={form.reason}
                  error={Boolean(form.error)}
                  helperText={[
                    form.error && translate(form.error, { max: CLOSURE_REASON_MAX }),
                    t('reasonHint', { count, max: CLOSURE_REASON_MAX }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  onChange={(e) => form.setReason(e.target.value)}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={form.close} disabled={form.submitting}>
              {t('back')}
            </Button>
            <LoadingButton
              variant="contained"
              color={mode === 'cancel' ? 'primary' : 'error'}
              onClick={form.submit}
              disabled={form.submitting}
              loading={form.submitting}
            >
              {form.alert ? t('retry') : t(`${mode}Confirm`)}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
